/**
 * Validation rules that BOTH the API and the UI must agree on.
 *
 * Anything here is enforced server-side. The front end imports the same
 * functions purely so the user gets feedback before they submit — it is never
 * the enforcement point.
 */
import { z } from 'zod';
import { LANGUAGES, ROLES, TIERS, PLANS, PRIORITIES, MFA_METHODS } from './enums.js';
// ---------------------------------------------------------------------------
//  Domain and e-mail
// ---------------------------------------------------------------------------
/**
 * A minimal public-suffix awareness. A full PSL is overkill here, but without
 * *some* multi-part-TLD knowledge we would wrongly reduce "company.co.uk" to
 * "co.uk" and then accept a contact address at the wrong organisation entirely.
 * Extend this list rather than reaching for a heavyweight dependency.
 */
const MULTI_PART_TLDS = [
    'co.uk', 'org.uk', 'ac.uk', 'gov.uk', 'me.uk', 'net.uk',
    'com.au', 'net.au', 'org.au', 'edu.au', 'gov.au',
    'co.nz', 'co.za', 'co.jp', 'or.jp', 'ne.jp',
    'com.br', 'com.mx', 'com.tr', 'com.cy', 'com.gr',
    'gov.gr', 'edu.gr', 'org.gr', 'net.gr',
    'com.sg', 'com.hk', 'com.cn', 'org.cn', 'net.cn',
];
const DOMAIN_RE = /^(?=.{1,253}$)(?!-)[a-z0-9-]{1,63}(?<!-)(\.(?!-)[a-z0-9-]{1,63}(?<!-))+$/;
/**
 * Normalise user input into a comparable domain: trim, lowercase, strip
 * scheme, path, query and a leading "www.".
 *
 * NOTE ON "www": it is stripped rather than rejected, so `www.example.com` is
 * accepted and stored as `example.com`. That is deliberate — `www` is a
 * universal convention for the same site, not a meaningfully different
 * organisation, and rejecting it would only teach users to delete four
 * characters by hand. Every OTHER subdomain (`mail.example.com`,
 * `eu.example.com`) is still rejected with a suggested root, because those can
 * genuinely denote different systems or business units.
 */
export function normalizeDomain(input) {
    let d = input.trim().toLowerCase();
    d = d.replace(/^[a-z]+:\/\//, ''); // strip scheme
    d = d.split('/')[0] ?? d; // strip path
    d = d.split('?')[0] ?? d; // strip query
    d = d.split('@').pop() ?? d; // tolerate a pasted e-mail address
    d = d.replace(/\.$/, ''); // strip the root dot
    if (d.startsWith('www.'))
        d = d.slice(4);
    return d;
}
/**
 * Reduce a hostname to its registrable root: "mail.eu.example.co.uk" → "example.co.uk".
 * Used to reject subdomains and to check that a contact e-mail belongs to the vendor.
 */
export function rootDomain(input) {
    const d = normalizeDomain(input);
    const parts = d.split('.');
    if (parts.length <= 2)
        return d;
    const lastTwo = parts.slice(-2).join('.');
    if (MULTI_PART_TLDS.includes(lastTwo) && parts.length >= 3) {
        return parts.slice(-3).join('.');
    }
    return lastTwo;
}
/**
 * Vendors and organisations are identified by a ROOT domain. Accepting
 * subdomains would let two records describe the same company and would break
 * the contact-email check below.
 */
export function checkRootDomain(input) {
    const d = normalizeDomain(input);
    if (!DOMAIN_RE.test(d))
        return { ok: false, reason: 'INVALID' };
    const root = rootDomain(d);
    if (root !== d)
        return { ok: false, reason: 'SUBDOMAIN', suggestedRoot: root };
    return { ok: true };
}
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function isValidEmail(input) {
    return EMAIL_RE.test(input.trim());
}
/**
 * A vendor assessment is a request for sensitive information. Requiring the
 * recipient address to sit on the vendor's own domain means an assessment
 * cannot be quietly redirected to an unrelated mailbox — a small control that
 * removes a whole class of social-engineering.
 */
export function emailMatchesDomain(email, domain) {
    if (!isValidEmail(email))
        return false;
    const emailDomain = normalizeDomain(email.split('@')[1] ?? '');
    return rootDomain(emailDomain) === rootDomain(domain);
}
// ---------------------------------------------------------------------------
//  Passwords
// ---------------------------------------------------------------------------
/**
 * 12 characters minimum with mixed classes. The 128 ceiling is not a strength
 * limit — it bounds the work Argon2 will do, so an enormous password cannot be
 * used to exhaust CPU.
 */
export const PASSWORD_MIN = 12;
export const PASSWORD_MAX = 128;
export const passwordSchema = z
    .string()
    .min(PASSWORD_MIN)
    .max(PASSWORD_MAX)
    .refine((v) => /[a-z]/.test(v), { message: 'PASSWORD_NEEDS_LOWERCASE' })
    .refine((v) => /[A-Z]/.test(v), { message: 'PASSWORD_NEEDS_UPPERCASE' })
    .refine((v) => /[0-9]/.test(v), { message: 'PASSWORD_NEEDS_DIGIT' })
    .refine((v) => /[^A-Za-z0-9]/.test(v), { message: 'PASSWORD_NEEDS_SYMBOL' });
// ---------------------------------------------------------------------------
//  Reusable field schemas
// ---------------------------------------------------------------------------
export const emailSchema = z.string().trim().toLowerCase().refine(isValidEmail, {
    message: 'INVALID_EMAIL',
});
export const rootDomainSchema = z
    .string()
    .trim()
    .transform(normalizeDomain)
    .superRefine((value, ctx) => {
    const check = checkRootDomain(value);
    if (check.ok)
        return;
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: check.reason === 'SUBDOMAIN'
            ? `SUBDOMAIN_NOT_ALLOWED:${check.suggestedRoot}`
            : 'INVALID_DOMAIN',
    });
});
export const languageSchema = z.enum(LANGUAGES);
export const roleSchema = z.enum(ROLES);
export const tierSchema = z.enum(TIERS);
export const planSchema = z.enum(PLANS);
export const prioritySchema = z.enum(PRIORITIES);
export const mfaMethodSchema = z.enum(MFA_METHODS);
/** cuid-shaped identifier. Rejects path traversal and SQL fragments by shape. */
export const idSchema = z.string().regex(/^[a-z0-9]{20,32}$/, 'INVALID_ID');
/**
 * A boolean that arrives as a query-string value.
 *
 * DO NOT USE `z.coerce.boolean()` FOR THIS. Zod's coercion is `Boolean(value)`,
 * and in JavaScript every non-empty string is truthy — so the string `"false"`
 * coerces to `true`. A filter written as `?isActive=false` would therefore
 * return exactly the rows the user was trying to exclude, silently and with no
 * error to notice. This has already shipped three times in this codebase.
 *
 * This parser accepts the spellings that actually turn up in URLs and rejects
 * anything else outright, so a typo produces a 400 rather than a wrong answer.
 */
export const booleanQueryParam = z
    .union([z.boolean(), z.string()])
    .transform((value, ctx) => {
    if (typeof value === 'boolean')
        return value;
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'on'].includes(normalized))
        return true;
    if (['false', '0', 'no', 'off'].includes(normalized))
        return false;
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'INVALID_BOOLEAN' });
    return z.NEVER;
});
/** Standard list query: page, page size, free-text search. */
export const paginationSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
    search: z.string().trim().max(200).optional(),
});
//# sourceMappingURL=validation.js.map